import type { Context } from '../integrations/context-types';

/** Stable identifier for a derived Context. Two derived chips that
 *  represent the same logical thing (e.g. the same entity on the same
 *  route) collapse to the same fingerprint — used for dismissal memory
 *  and for the dedup pass that survives re-derives. */
export function derivedFingerprint(c: Context): string {
  if (c.kind === 'screen' && c.entityType && c.entityId) {
    return `entity:${c.entityType}:${c.entityId}`;
  }
  if (c.kind === 'screen') return `screen:${c.view}:${c.route ?? ''}`;
  return `${c.kind}:${JSON.stringify(c)}`;
}

/** Stable identifier for a user-attachable Context — used to dedup the
 *  hydrated pinned list against any contexts the user has already
 *  attached in the live session. Cross-referenced against the derived
 *  variant: an entity entry has the same shape in both lanes, so the
 *  identifier collapses to the same string and a pin-then-hydrate
 *  doesn't double up. */
export function userContextFingerprint(c: Context): string {
  if (c.kind === 'entity') return `entity:${c.entityType}:${c.entityId}`;
  if (c.kind === 'document') return `document:${c.fileName}:${c.url}`;
  if (c.kind === 'selection')
    return `selection:${c.sourceView}:${c.content.slice(0, 64)}`;
  if (c.kind === 'screen' && c.entityType && c.entityId)
    return `entity:${c.entityType}:${c.entityId}`;
  if (c.kind === 'screen') return `screen:${c.view}:${c.route ?? ''}`;
  return `${(c as { kind: string }).kind}:${JSON.stringify(c)}`;
}
